import { Injectable } from "@angular/core";
import { NextPageService, NextPageRoute } from "@cvx/nextpage";
import { BehaviorSubject, map, Observable, tap } from "rxjs";
import { UserContextService } from "./common/user-context.service";
import { RouteAuthorizationData } from "../models/authorization/route-authorization-data.interface";
import { AccessType } from "../models/authorization/access-type.enum";

/**
 * Service responsible for filtering and managing application routes
 * Filters menu items based on user permissions using the same logic as RoleGuard
 */
@Injectable({
  providedIn: "root",
})
export class RoutesService {
  private filteredRoutes: NextPageRoute[] = [];

  /**
   * Observable that emits when filtered routes are loaded and ready
   */
  filteredRoutesLoaded = new BehaviorSubject<boolean>(false);

  constructor(
    private userContextService: UserContextService,
    private NP: NextPageService
  ) {}

  /**
   * Sets the filtered routes (for internal use)
   */
  setFilteredRoutes(routes: NextPageRoute[]) {
    this.filteredRoutes = routes;
  }

  /**
   * Gets the currently filtered routes
   */
  getFilteredRoutes(): NextPageRoute[] {
    return this.filteredRoutes;
  }

  /**
   * Filters routes based on user permissions
   * @param routes - Array of routes to filter
   * @returns Observable of filtered routes
   */
  public filterRoutes(routes: NextPageRoute[]): Observable<NextPageRoute[]> {
    // Wait for user context to load
    return this.userContextService.waitForLoad().pipe(
      map(() => {
        const filtered = routes
          .map(route => this.filterRoute(route))
          .filter(route => route !== null) as NextPageRoute[];
      
        return filtered;
      })
    );
  }

  /**
   * Filters routes for menu display
   * Applies filtering and updates NextPage navigation
   * @param routes - Array of routes to filter for menu
   */
  public filterRoutesForMenu(routes: NextPageRoute[]) {
    this.filterRoutes(routes).pipe(
      tap(filteredRoutes => {
        this.filteredRoutes = filteredRoutes;

        if (filteredRoutes.length === 0) {
          this.filteredRoutesLoaded.next(true);
          return;
        }

        // Update NextPage with filtered routes
        this.NP.setOption("navigationRoutes", filteredRoutes);
        this.NP.options.navigationRoutes = filteredRoutes;
        this.NP.UI.options.navigationRoutes = filteredRoutes;
        this.NP.buildMenuStructure();
        
        this.filteredRoutesLoaded.next(true);
      })
    ).subscribe({
      next: () => undefined,
      error: (err) => {
        console.error('RoutesService: Error during filtering', err);
        this.filteredRoutesLoaded.next(true); // Still allow app to load
      },
      complete: () => undefined
    });
  }

  /**
   * Filters a single route based on user permissions
   * Uses the same authorization logic as RoleGuard
   * @param route - Route to filter
   * @returns Filtered route or null if access denied
   */
  private filterRoute(route: NextPageRoute): NextPageRoute | null {
    // Create a copy to avoid mutating original
    const routeCopy = { ...route };

    // Check authorization requirements
    const authData: RouteAuthorizationData = routeCopy.data || {};

    // If no requiredPermissionKey, include the route
    if (!authData.requiredPermissionKey) {
      // Still need to filter children if they exist
      if (routeCopy.children && routeCopy.children.length > 0) {
        routeCopy.children = routeCopy.children
          .map(child => this.filterRoute(child))
          .filter(child => child !== null) as NextPageRoute[];
      }
      return routeCopy;
    }

    // Check permission requirements
    const requiredAccess = authData.requiredAccess || AccessType.Read;
    const hasAccess = this.userContextService.hasAccess(
      authData.requiredPermissionKey,
      requiredAccess
    );

    if (!hasAccess) {
      return null;
    }

    // Access granted - filter children recursively
    if (routeCopy.children && routeCopy.children.length > 0) {
      routeCopy.children = routeCopy.children
        .map(child => this.filterRoute(child))
        .filter(child => child !== null) as NextPageRoute[];
      
      // If all children were filtered out, consider hiding the parent too
      if (routeCopy.children.length === 0 && route.children && route.children.length > 0) {
        return null;
      }
    }

    return routeCopy;
  }

  /**
   * Checks if a specific route is accessible by current user
   * @param route - Route to check
   * @returns true if route is accessible
   */
  public isRouteAccessible(route: NextPageRoute): boolean {
    return this.filterRoute(route) !== null;
  }

  /**
   * Legacy support: checks access using old pageName/subPageName format
   * @deprecated Use isRouteAccessible with new route format instead
   */
  public hasAccessToPage(pageName: string, subPageName: string): boolean {
    // Try to find matching permission key
    const permissionKey = `${pageName}.${subPageName}.Page`;
    return this.userContextService.hasAccess(permissionKey, AccessType.Read);
  }
}
